import { observable, action, computed, makeObservable } from 'mobx'

import type { Nullable } from 'types/helpers'
import { ShopRootStore } from 'shop/stores/shopRoot.store'

import {
  ApiResult,
  hasErrors,
  Personalisation,
  PersonalisationAttendee,
  PersonalisationAttendeeUpdate,
  PersonalisationErrorType,
  PersonalisationsApi,
  createPersonalisationsApi,
} from 'shop/api/personalisations.api'

export type { PersonalisationErrorType }

const ATTENDEE_TYPE = 'personalisation_attendees'

export class PersonalisationsStore {
  storeName = 'PersonalisationsStore'
  childApi: PersonalisationsApi

  constructor(root: ShopRootStore) {
    this.childApi = createPersonalisationsApi(root.apiClientV2)
    makeObservable(this)
  }

  @observable loading = true
  @observable submitting = false
  @observable item: Nullable<Personalisation> = null
  @observable attendee: Nullable<PersonalisationAttendee> = null
  @observable errorType: Nullable<PersonalisationErrorType> = null
  @observable fieldErrors: Record<string, string[]> = {}
  verificationToken: Nullable<string> = null
  // Personalisation code the attendee typed, not the scan code printed on the ticket.
  @observable verifiedCode: Nullable<string> = null

  @observable transferredTo: Nullable<string> = null
  // Store is a module-level singleton. Success card only shows when this matches the route token.
  @observable transferredToken: Nullable<string> = null
  // Transfer refusals stay here so they do not repaint the form.
  @observable transferErrorType: Nullable<PersonalisationErrorType> = null

  // Set only on load. Deriving from errorType would unmount the form on a refused save.
  @observable private seatMissing = false

  @computed get notFound() {
    return this.seatMissing
  }

  // Set only on load, same reason as seatMissing.
  @observable private platformClosed = false

  @computed get temporarilyClosed() {
    return this.platformClosed
  }

  @action fetchItem = async (username: string, token: string) => {
    if (this.transferredToken && this.transferredToken !== token) {
      this.transferredTo = null
      this.transferredToken = null
    }

    this.loading = true
    const resp = await this.childApi.fetchItem(username, token)
    const loaded = this.applyResponse(resp)
    this.seatMissing = !loaded && this.errorType === 'not_found'
    this.platformClosed = !loaded && this.errorType === 'temporarily_closed'
    this.loading = false
    return resp
  }

  @action verify = async (username: string, token: string, code: string, recaptcha?: string) => {
    this.submitting = true
    const resp = await this.childApi.verify(username, token, code, recaptcha)
    const verified = this.applyResponse(resp)

    if (verified) {
      this.verificationToken = this.item?.verificationToken ?? null
      this.verifiedCode = code
    }

    this.platformClosed = !verified && this.errorType === 'temporarily_closed'
    this.submitting = false
    return resp
  }

  // Transfer does not go through applyResponse: 204 has no personalisation, refusals go in transferErrorType.
  @action transferPersonalisation = async (
    username: string,
    token: string,
    details: { email: string; emailConfirmation: string; code: string },
    recaptcha?: string
  ) => {
    this.submitting = true
    const resp = await this.childApi.transfer(username, token, details, this.verificationToken ?? undefined, recaptcha)

    if (!hasErrors(resp) && this.isCommitted(resp)) {
      this.transferredTo = details.email.trim().toLowerCase()
      this.transferredToken = token
      this.transferErrorType = null
    } else if (!hasErrors(resp)) {
      this.transferErrorType = 'unexpected'
    } else {
      const [first] = resp.errors
      this.transferErrorType = first?.meta?.type ?? this.errorTypeFromStatus(resp.status)
    }

    this.submitting = false
    return resp
  }

  // 204 is a status check, never a data check. Missing status is still accepted (bodiless success).
  private isCommitted = (resp: { status?: number } | undefined): boolean => {
    const status = resp?.status
    return status === undefined || status === null || status === 204
  }

  @action setTransferErrorType = (type: PersonalisationErrorType) => {
    this.transferErrorType = type
  }

  @action setErrorType = (type: PersonalisationErrorType) => {
    this.errorType = type
  }

  @action resetTransferError = () => {
    this.transferErrorType = null
  }

  @action resetTransferResult = () => {
    this.transferredTo = null
    this.transferredToken = null
  }

  // Must not touch seatMissing or platformClosed: a refused SAVE must keep the form mounted.
  @action updateAttendee = async (username: string, token: string, attendee: PersonalisationAttendeeUpdate) => {
    this.submitting = true
    const resp = await this.childApi.updateAttendee(username, token, attendee, this.verificationToken ?? undefined)
    this.applyResponse(resp)
    this.submitting = false
    return resp
  }

  @action private applyResponse = (resp: ApiResult<Personalisation> | undefined): boolean => {
    if (!hasErrors(resp)) {
      this.item = resp?.data ?? null
      this.attendee = (resp?.included?.find((row) => row?.type === ATTENDEE_TYPE)?.attributes ??
        null) as Nullable<PersonalisationAttendee>
      this.errorType = null
      this.fieldErrors = {}
      return true
    }

    const [first] = resp.errors

    this.errorType = first?.meta?.type ?? this.errorTypeFromStatus(resp.status)
    this.fieldErrors = resp.errors.reduce<Record<string, string[]>>((acc, error) => {
      const attribute = error?.meta?.attribute
      if (!attribute) return acc

      return { ...acc, [attribute]: [...(acc[attribute] ?? []), error.detail ?? error.title ?? ''] }
    }, {})

    return false
  }

  private errorTypeFromStatus = (status?: number): Nullable<PersonalisationErrorType> => {
    if (status === 404) return 'not_found'
    if (status === 503) return 'temporarily_closed'
    if (status === 429) return 'rate_limited'
    if (status === 422) return 'invalid_code'
    // 403 is both expired grant and past deadline; default to not_verified (form already disables save when read-only).
    if (status === 403) return 'not_verified'
    if (status === 409) return 'conflict'

    // A null here on a refused request renders a blank screen.
    return 'unexpected'
  }

  @action hydrate = (key: string, data: Personalisation) => {
    if (key === 'item') this.item = data
  }
}
